import { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';

// Componentes
import Breadcrumbs from '../../components/Breadcrumbs/Breadcrumbs';
import FriendlyPhrase from '../../components/FriendlyPhrase/FriendlyPhrase';

// Contextos
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { useLoader } from '../../contexts/LoaderContext';

// Estilos
import styles from './empresaformpage.module.css';

// API
import { useEmpresaService } from '../../services/empresaServices';

// Constantes
import { URLS } from '../../constants/urls';

function EmpresaFormPage() {
    const { logout } = useAuth();
    const { showToast } = useToast();
    const { showLoader, hideLoader } = useLoader();
    const navigate = useNavigate();

    const { criarEmpresa, atualizarEmpresa, obterEmpresaPorId } = useEmpresaService();
    
    const { idEmpresa } = useParams();
    const { pathname } = useLocation();

    const isEditing = !!idEmpresa && pathname.includes('atualizar');

    const [formData, setFormData] = useState({
        nome: '',
        logo: '',
        setor: '',
        logoFile: null,
    });

    const [logoPreview, setLogoPreview] = useState(null);

    useEffect(() => {
        if (isEditing) {
            const carregarEmpresa = async () => {
                showLoader("Carregando dados da empresa");

                const response = await obterEmpresaPorId(idEmpresa);

                if (response.success) {
                    const empresa = response.data;

                    setFormData({
                        nome: empresa.nome || '',
                        setor: empresa.setor || '',
                        logo: empresa.logo || '',
                        logoFile: null,
                    });

                    if (empresa.logo) {
                        const imgPath = `${URLS.IMG_PATH}${empresa.logo}`;
                        setLogoPreview(imgPath);
                    }
                } else {
                    alert('Erro ao carregar empresa: ' + response.message);
                    if (response.message?.toLowerCase().includes('token')) logout();
                }

                hideLoader();
            };

            carregarEmpresa();
        }
    }, [idEmpresa, isEditing]);

    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value,
        });
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];

        if (file && file.type.startsWith('image/')) {
            setFormData((prev) => ({ ...prev, logoFile: file }));

            const reader = new FileReader();

            reader.onloadend = () => setLogoPreview(reader.result);
            reader.readAsDataURL(file);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        showLoader("Enviando dados da empresa");

        const form = new FormData();

        form.append('nome', formData.nome);
        form.append('setor', formData.setor);

        if (formData.logoFile) {
            form.append('logo', formData.logoFile);
        }

        if (isEditing) form.append('id', idEmpresa);

        const response = isEditing
            ? await atualizarEmpresa(form)
            : await criarEmpresa(form);

        if (response.success) {
            showToast((isEditing ? 'Empresa editada com sucesso!' : 'Empresa criada com sucesso!'), 3000, 'success');

            navigate('/empresas');
        } else {
            showToast((isEditing ? 'Erro ao atualizar.' : 'Erro ao criar.'), 3000, 'error');

            console.log(response.message);

            if (response.message?.toLowerCase().includes('token')) logout();
        }

        hideLoader();
    };

    const breadcrumbLabel = isEditing
        ? `${formData.nome || 'empresa'}`
        : undefined;

    return (
        <div className={styles.EmpresaFormPage}>
            <div className={styles.containerTop}>
                <Breadcrumbs idLabels={breadcrumbLabel ? [{ id: idEmpresa, label: breadcrumbLabel }] : []} />

                <h1 className={styles.title}>
                    {isEditing ? 'Editar Empresa' : 'Cadastro de Empresa'}
                </h1>

                {isEditing ? <FriendlyPhrase page={"atualizarEmpresa"}/> : <FriendlyPhrase page={"adicionarEmpresa"}/>}
            </div>

            <form onSubmit={handleSubmit} className={styles.formulario}>
                <label>
                    Nome:
                    <input
                        type="text"
                        name="nome"
                        value={formData.nome}
                        onChange={handleChange}
                        required
                    />
                </label>

                <label>
                    Logo:
                    <div className={styles.fileInputWrapper}>
                        <input
                        type="file"
                        accept="image/*"
                        onChange={handleFileChange}
                        className={styles.fileInput}
                        />
                        <span className={styles.fileLabel}>Escolher imagem</span>
                    </div>
                </label>

                {logoPreview && (
                    <img
                        src={logoPreview}
                        alt="Pré-visualização da logo"
                        className={styles.logoPreview}
                    />
                )}

                <label>
                    Setor:
                    <input
                        type="text"
                        name="setor"
                        value={formData.setor}
                        onChange={handleChange}
                    />
                </label>

                <div className={styles.submitArea}>
                    <button type="submit" className={styles.submitButton}>
                        {isEditing ? 'Atualizar Empresa' : 'Salvar Empresa'}
                    </button>
                </div>
            </form>
        </div>
    );
}

export default EmpresaFormPage;